import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  BookOpen,
  Image,
  Tag,
  Calendar,
  Eye,
  Star,
  Globe,
  X
} from 'lucide-react';
import { BASE_URL } from '../config';

// Import jQuery and Summernote
import $ from 'jquery';
import 'summernote/dist/summernote-lite.css';
import 'summernote/dist/summernote-lite.js';

// Make jQuery available globally for Summernote
window.$ = window.jQuery = $;

export default function BlogContent({ onBack, mode = 'create' }) {
  const { postId } = useParams();
  
  // Initialize form data function
  const initializeFormData = (data = null) => {
    return {
      title: data?.title || '',
      slug: data?.slug || '',
      excerpt: data?.excerpt || '',
      content: data?.content || '',
      featured_image: data?.featured_image || '',
      featured_image_alt: data?.featured_image_alt || '',
      preview_image: data?.preview_image || '',
      category: data?.category?.id || '',
      tags: data?.tags || '',
      seo_title: data?.seo_title || '',
      seo_description: data?.seo_description || '',
      seo_keywords: data?.seo_keywords || '',
      og_title: data?.og_title || '',
      og_description: data?.og_description || '',
      og_image: data?.og_image || '',
      twitter_title: data?.twitter_title || '',
      twitter_description: data?.twitter_description || '',
      twitter_image: data?.twitter_image || '',
      twitter_card_type: data?.twitter_card_type || 'summary_large_image',
      canonical_url: data?.canonical_url || '',
      robots_meta: data?.robots_meta || 'index, follow',
      structured_data: data?.structured_data ? JSON.stringify(data.structured_data, null, 2) : '{}',
      status: data?.status || 'draft',
      is_featured: data?.is_featured !== undefined ? data.is_featured : false,
      // Remove published_at from form data - it will be auto-set by backend
      read_time_minutes: data?.read_time_minutes || 5,
      priority: data?.priority || 6
    };
  };
  
  const [formData, setFormData] = useState(() => initializeFormData());
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const summernoteRef = useRef(null);

  // Helper to determine if form should be read-only
  const isReadOnly = mode === 'view';

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' }
  ];

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000); // Shorter timeout for toast
  };

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  // Handle status change
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setFormData(prev => ({
      ...prev,
      status: newStatus
      // published_at will be auto-set by backend when status is 'published'
    }));
  };

  // Handle title change and auto-generate slug
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: mode === 'create' ? generateSlug(title) : prev.slug
    }));
  };

  // Initialize Summernote
  const initializeSummernote = () => {
    if (summernoteRef.current && window.$) {
      console.log('🔧 Initializing Summernote editor...');
      
      // Destroy existing instance if it exists
      if (window.$(summernoteRef.current).hasClass('note-editor')) {
        console.log('🗑️ Destroying existing Summernote instance');
        window.$(summernoteRef.current).summernote('destroy');
      }

      try {
        window.$(summernoteRef.current).summernote({
          height: 400,
          minHeight: 200,
          maxHeight: 800,
          focus: true,
          placeholder: 'Enter your content here...',
          fontNames: ['Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Helvetica Neue', 'Helvetica', 'Impact', 'Lucida Grande', 'Nunito', 'Tahoma', 'Times New Roman', 'Verdana'],
          fontNamesIgnoreCheck: ['Nunito'],
          toolbar: [
            ['style', ['style']],
            ['font', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
            ['fontname', ['fontname']],
            ['fontsize', ['fontsize']],
            ['color', ['color']],
            ['para', ['ul', 'ol']],
            ['para', ['paragraph']],
            ['table', ['table']],
            ['insert', ['link', 'picture', 'hr']],
            ['view', ['fullscreen', 'codeview', 'help']]
          ],
          callbacks: {
            onChange: function(contents) {
              if (!isReadOnly) {
                clearTimeout(window.summernoteTimeout);
                window.summernoteTimeout = setTimeout(() => {
                  setFormData(prev => {
                    if (prev.content !== contents) {
                      const wordCount = contents.replace(/<[^>]*>/g, '').split(/\s+/).length;
                      const readingTime = Math.max(1, Math.round(wordCount / 225));
                      return { 
                        ...prev, 
                        content: contents,
                        read_time_minutes: readingTime
                      };
                    }
                    return prev;
                  });
                }, 300);
              }
            },
            onInit: function() {
              console.log('✅ Summernote initialized successfully');
              
              // Set initial content or ensure there's at least a paragraph
              if (formData.content) {
                console.log('📝 Setting initial content');
                window.$(summernoteRef.current).summernote('code', formData.content);
              } else {
                // Initialize with empty paragraph
                window.$(summernoteRef.current).summernote('code', '<p><br></p>');
              }
              
              // Fix list buttons - add proper click handlers
              setTimeout(() => {
                const editor = window.$(summernoteRef.current);
                const noteEditor = editor.next('.note-editor');
                const editableDiv = noteEditor.find('.note-editable');
                
                // Ensure contenteditable
                editableDiv.attr('contenteditable', 'true');
                
                // Try multiple selectors for list buttons
                const ulSelectors = [
                  'button[data-name="insertUnorderedList"]',
                  'button[data-event="insertUnorderedList"]',
                  '.note-btn[data-name="ul"]',
                  '.note-para button:contains("•")',
                  '.note-para .note-btn:eq(0)'
                ];
                
                const olSelectors = [
                  'button[data-name="insertOrderedList"]',
                  'button[data-event="insertOrderedList"]',
                  '.note-btn[data-name="ol"]',
                  '.note-para button:contains("1.")',
                  '.note-para .note-btn:eq(1)'
                ];
                
                let ulButton, olButton;
                
                // Find UL button
                for (const selector of ulSelectors) {
                  ulButton = noteEditor.find(selector);
                  if (ulButton.length) {
                    console.log('🔍 Found UL button with selector:', selector);
                    break;
                  }
                }
                
                // Find OL button
                for (const selector of olSelectors) {
                  olButton = noteEditor.find(selector);
                  if (olButton.length) {
                    console.log('🔍 Found OL button with selector:', selector);
                    break;
                  }
                }
                
                // Override UL button
                if (ulButton && ulButton.length) {
                  ulButton.off('click').on('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('📝 UL button clicked');
                    
                    editableDiv.focus();
                    
                    // Ensure there's a selection
                    const selection = window.getSelection();
                    if (!selection.rangeCount) {
                      const range = document.createRange();
                      range.selectNodeContents(editableDiv[0]);
                      range.collapse(false);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                    
                    document.execCommand('insertUnorderedList', false, null);
                    editor.summernote('triggerEvent', 'change');
                  });
                } else {
                  console.warn('⚠️ UL button not found');
                }
                
                // Override OL button
                if (olButton && olButton.length) {
                  olButton.off('click').on('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('📝 OL button clicked');
                    
                    editableDiv.focus();
                    
                    // Ensure there's a selection
                    const selection = window.getSelection();
                    if (!selection.rangeCount) {
                      const range = document.createRange();
                      range.selectNodeContents(editableDiv[0]);
                      range.collapse(false);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                    
                    document.execCommand('insertOrderedList', false, null);
                    editor.summernote('triggerEvent', 'change');
                  });
                } else {
                  console.warn('⚠️ OL button not found');
                }
                
                // Set initial focus
                editableDiv.focus();
              }, 200);
            },
            onFocus: function() {
              const editableDiv = window.$(summernoteRef.current).next('.note-editor').find('.note-editable');
              if (editableDiv.length && editableDiv.html().trim() === '') {
                window.$(summernoteRef.current).summernote('code', '<p><br></p>');
              }
            }
          },
          disabled: isReadOnly
        });
        
        console.log('✅ Summernote configuration applied');
      } catch (error) {
        console.error('❌ Error initializing Summernote:', error);
      }
    } else {
      console.warn('⚠️ Summernote ref or jQuery not available');
    }
  };

  // Destroy Summernote
  const destroySummernote = () => {
    if (summernoteRef.current && window.$) {
      // Clear any pending timeout
      if (window.summernoteTimeout) {
        clearTimeout(window.summernoteTimeout);
        window.summernoteTimeout = null;
      }
      
      // Check if summernote is initialized before destroying
      if (window.$(summernoteRef.current).hasClass('note-editor')) {
        window.$(summernoteRef.current).summernote('destroy');
      }
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const response = await fetch(`${baseUrl}/api/seo/admin/blog/categories/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch blog post content for edit/view mode
  const fetchBlogContent = async () => {
    if ((mode !== 'edit' && mode !== 'view') || !postId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const response = await fetch(`${baseUrl}/api/seo/admin/blog/posts/${postId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(initializeFormData(data));
      } else {
        const errorText = await response.text();
        console.error('Fetch error response:', errorText);
        showNotification('Failed to fetch blog post', 'error');
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      showNotification('Network error: Unable to fetch blog post', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        setSaving(false);
        return;
      }

      const method = mode === 'create' ? 'POST' : 'PUT';
      
      // Get current content from Summernote editor before validation
      let currentContent = formData.content;
      if (summernoteRef.current && window.$ && window.$.fn.summernote) {
        currentContent = window.$(summernoteRef.current).summernote('code');
        console.log('📝 Captured content from Summernote:', currentContent.substring(0, 100) + '...');
      }
      
      // Validate required fields
      if (!formData.title || !currentContent || !formData.excerpt) {
        showNotification('Please fill in all required fields (Title, Content, Excerpt)', 'error');
        setSaving(false);
        return;
      }

      // Parse structured data
      let parsedStructuredData = {};
      try {
        parsedStructuredData = JSON.parse(formData.structured_data || '{}');
      } catch (error) {
        showNotification('Invalid JSON in structured data', 'error');
        setSaving(false);
        return;
      }

      const submitData = {
        ...formData,
        content: currentContent, // Use the captured content from Summernote
        structured_data: parsedStructuredData,
        category: formData.category || null
        // published_at will be auto-set by backend when status is 'published'
      };

      console.log('📤 Submitting blog post with content length:', currentContent.length);

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      
      const url = mode === 'create' 
        ? `${baseUrl}/api/seo/admin/blog/posts/create/`
        : `${baseUrl}/api/seo/admin/blog/posts/${postId}/`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        showNotification(
          mode === 'create' ? 'Blog post created successfully' : 'Blog post updated successfully'
        );
        
        // Redirect after showing toast
        setTimeout(() => {
          onBack();
        }, 2000); // Shorter delay to see the toast
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage = `Operation failed (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Network error:', error);
      showNotification('Network error: Unable to save blog post', 'error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    
    if ((mode === 'edit' || mode === 'view') && postId) {
      fetchBlogContent();
    }
  }, [mode, postId]);

  // Initialize Summernote - only after loading is complete
  useEffect(() => {
    // Only initialize when not in read-only mode AND not loading
    if (!isReadOnly && !loading && summernoteRef.current) {
      console.log('🔄 Initializing Summernote from npm package...');
      
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeSummernote();
      }, 100);

      return () => {
        clearTimeout(timer);
        destroySummernote();
      };
    }
  }, [isReadOnly, loading, formData.content]); // Depend on isReadOnly, loading, and content

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          {/* Back to Blog Management */}
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {mode === 'create' ? 'Create Blog Post' : mode === 'edit' ? 'Edit Blog Post' : 'View Blog Post'}
          </h1>
          <p className="text-slate-600">
            {mode === 'create' ? 'Create a new blog post' : mode === 'edit' ? 'Update blog post details' : 'View blog post details'}
          </p>
        </div>
      </div>

      {/* Toast Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.3 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.3 }}
          className={`fixed top-4 right-4 z-50 rounded-lg shadow-lg overflow-hidden min-w-[300px] max-w-[400px] ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="p-4 flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Progress bar */}
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: 3, ease: "linear" }}
            className={`h-1 ${
              notification.type === 'success' 
                ? 'bg-green-400' 
                : 'bg-red-400'
            }`}
          />
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-medical-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Basic Information</h2>
              <p className="text-sm text-slate-600">Essential blog post information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Title * <span className="text-xs text-slate-500">({formData.title?.length || 0}/200 characters)</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                readOnly={isReadOnly}
                maxLength={200}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                required
                placeholder="Enter your blog post title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Slug * <span className="text-xs text-slate-500">(URL-friendly version)</span>
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                required
                placeholder="blog-post-url-slug"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Excerpt * <span className="text-xs text-slate-500">({formData.excerpt?.length || 0}/300 characters)</span>
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                readOnly={isReadOnly}
                maxLength={300}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                required
                placeholder="Brief description for blog cards and previews"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Content * <span className="text-xs text-slate-500">(Rich Text Editor)</span>
              </label>
              {isReadOnly ? (
                <div 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 min-h-[400px]"
                  dangerouslySetInnerHTML={{ __html: formData.content }}
                />
              ) : (
                <div>
                  <div ref={summernoteRef}></div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:bg-slate-100"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={handleStatusChange}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:bg-slate-100"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reading Time (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.read_time_minutes}
                  onChange={(e) => setFormData({ ...formData, read_time_minutes: parseInt(e.target.value) || 1 })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tags <span className="text-xs text-slate-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="health, medical, technology"
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-medical-600 focus:ring-medical-500 border-slate-300 rounded disabled:opacity-50"
                />
                <label htmlFor="is_featured" className="ml-2 block text-sm text-slate-700 flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Featured Post
                </label>
              </div>

              {/* {formData.status === 'published' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.published_at}
                    onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                    readOnly={isReadOnly}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                  />
                </div>
              )} */}
            </div>
          </div>
        </motion.div>

        {/* Media Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Media</h2>
              <p className="text-sm text-slate-600">Images and media for your blog post</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Featured Image URL
              </label>
              <input
                type="url"
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Featured Image Alt Text
              </label>
              <input
                type="text"
                value={formData.featured_image_alt}
                onChange={(e) => setFormData({ ...formData, featured_image_alt: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Descriptive alt text for accessibility"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Preview Image URL <span className="text-xs text-slate-500">(for cards and social sharing)</span>
              </label>
              <input
                type="url"
                value={formData.preview_image}
                onChange={(e) => setFormData({ ...formData, preview_image: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="https://example.com/preview.jpg"
              />
            </div>
          </div>
        </motion.div>

        {/* SEO Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">SEO Settings</h2>
              <p className="text-sm text-slate-600">Search engine optimization settings</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                SEO Title <span className="text-xs text-slate-500">({formData.seo_title?.length || 0}/60 recommended)</span>
              </label>
              <input
                type="text"
                value={formData.seo_title}
                onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use main title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                SEO Description <span className="text-xs text-slate-500">({formData.seo_description?.length || 0}/160 recommended)</span>
              </label>
              <textarea
                value={formData.seo_description}
                onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                readOnly={isReadOnly}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use excerpt"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                SEO Keywords <span className="text-xs text-slate-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.seo_keywords}
                onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="medical, health, technology"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Canonical URL
                </label>
                <input
                  type="url"
                  value={formData.canonical_url}
                  onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Priority (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 6 })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Open Graph Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Open Graph (Facebook)</h2>
              <p className="text-sm text-slate-600">Social media sharing settings for Facebook</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                OG Title <span className="text-xs text-slate-500">({formData.og_title?.length || 0}/60 recommended)</span>
              </label>
              <input
                type="text"
                value={formData.og_title}
                onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use SEO title or main title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                OG Description <span className="text-xs text-slate-500">({formData.og_description?.length || 0}/160 recommended)</span>
              </label>
              <textarea
                value={formData.og_description}
                onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                readOnly={isReadOnly}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use SEO description or excerpt"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                OG Image URL
              </label>
              <input
                type="url"
                value={formData.og_image}
                onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use preview image or featured image"
              />
            </div>
          </div>
        </motion.div>

        {/* Twitter Card Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Twitter Card</h2>
              <p className="text-sm text-slate-600">Social media sharing settings for Twitter</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Twitter Title <span className="text-xs text-slate-500">({formData.twitter_title?.length || 0}/70 recommended)</span>
              </label>
              <input
                type="text"
                value={formData.twitter_title}
                onChange={(e) => setFormData({ ...formData, twitter_title: e.target.value })}
                readOnly={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use OG title or main title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Twitter Description <span className="text-xs text-slate-500">({formData.twitter_description?.length || 0}/200 recommended)</span>
              </label>
              <textarea
                value={formData.twitter_description}
                onChange={(e) => setFormData({ ...formData, twitter_description: e.target.value })}
                readOnly={isReadOnly}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                placeholder="Leave empty to use OG description or excerpt"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Twitter Image URL
                </label>
                <input
                  type="url"
                  value={formData.twitter_image}
                  onChange={(e) => setFormData({ ...formData, twitter_image: e.target.value })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                  placeholder="Leave empty to use OG image"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Twitter Card Type
                </label>
                <select
                  value={formData.twitter_card_type}
                  onChange={(e) => setFormData({ ...formData, twitter_card_type: e.target.value })}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:bg-slate-100"
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">Summary Large Image</option>
                  <option value="app">App</option>
                  <option value="player">Player</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Advanced SEO Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Advanced SEO</h2>
              <p className="text-sm text-slate-600">Advanced search engine optimization settings</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Robots Meta Tag
              </label>
              <select
                value={formData.robots_meta}
                onChange={(e) => setFormData({ ...formData, robots_meta: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:bg-slate-100"
              >
                <option value="index, follow">Index, Follow (Default)</option>
                <option value="index, nofollow">Index, No Follow</option>
                <option value="noindex, follow">No Index, Follow</option>
                <option value="noindex, nofollow">No Index, No Follow</option>
                <option value="noarchive">No Archive</option>
                <option value="nosnippet">No Snippet</option>
                <option value="noimageindex">No Image Index</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Structured Data (JSON-LD) <span className="text-xs text-slate-500">Advanced users only</span>
              </label>
              <textarea
                value={formData.structured_data}
                onChange={(e) => setFormData({ ...formData, structured_data: e.target.value })}
                readOnly={isReadOnly}
                rows={8}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50 font-mono text-sm"
                placeholder='{"@context": "https://schema.org", "@type": "Article", "headline": "Your Article Title"}'
              />
              <p className="text-xs text-slate-500 mt-1">
                Valid JSON-LD structured data for rich snippets. Leave as empty object {} if unsure.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Submit Button - Hide in view mode */}
        {!isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-medical-500 text-white px-8 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {mode === 'create' ? 'Create Blog Post' : 'Update Blog Post'}
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Back Button for View Mode */}
        {isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="bg-slate-500 text-white px-8 py-2 rounded-lg hover:bg-slate-600 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {/* Back to Blog Management */}
            </button>
          </motion.div>
        )}
      </form>
    </div>
  );
}